import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, TextField, IconButton, Collapse,
  Button, ImageList, ImageListItem, Avatar
} from '@mui/material';
import { Build, ExpandMore, ExpandLess, Save, Block, Inventory2, Lock } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const statusMeta = {
  pending_review: { label: 'Pending Review', color: 'warning' },
  priced: { label: 'Priced', color: 'info' },
  added_to_order: { label: 'Added to Order', color: 'primary' },
  locked: { label: 'Locked', color: 'success' },
  declined: { label: 'Declined', color: 'error' }
};

const AdminCustomizationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [priceDraft, setPriceDraft] = useState({});
  const [saving, setSaving] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customization-requests/admin');
      setRequests(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const savePrice = async (id) => {
    const price = parseFloat(priceDraft[id]);
    if (Number.isNaN(price) || price < 0) { toast.error('Enter a valid price'); return; }
    setSaving(id);
    try {
      await api.put(`/customization-requests/${id}/price`, { customizationPrice: price });
      toast.success('Price saved');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save price'); }
    setSaving('');
  };

  const decline = async (id) => {
    setSaving(id);
    try {
      await api.put(`/customization-requests/${id}/decline`);
      toast.success('Request declined');
      load();
    } catch (err) { toast.error('Failed to decline'); }
    setSaving('');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        <Build sx={{ verticalAlign: 'middle', mr: 1 }} />
        Customization Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Customer requests to modify a catalog product, filed via Sam. Set a price so the customer can add it to
        their order — visible to the product's supplier too.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell />
              <TableCell>Product</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Filed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => {
              const meta = statusMeta[r.status] || { label: r.status, color: 'default' };
              const image = r.product?.images?.find((i) => i.isPrimary) || r.product?.images?.[0];
              const editable = !r.locked && r.status !== 'declined';
              return (
                <React.Fragment key={r._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpandedId(expandedId === r._id ? null : r._id)}>
                        {expandedId === r._id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {image?.url ? <Avatar src={image.url} variant="rounded" /> : <Avatar variant="rounded"><Inventory2 fontSize="small" /></Avatar>}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{r.product?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.supplier?.companyName}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {r.customer?.firstName} {r.customer?.lastName}
                      <Typography variant="caption" display="block" color="text.secondary">{r.customer?.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={meta.label} color={meta.color} icon={r.locked ? <Lock fontSize="small" /> : undefined} />
                    </TableCell>
                    <TableCell>
                      {typeof r.customizationPrice === 'number' ? `$${r.customizationPrice.toLocaleString()}` : '—'}
                    </TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedId === r._id} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Requested customization</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{r.description}</Typography>

                          {r.attachments?.length > 0 && (
                            <>
                              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Attachments</Typography>
                              <ImageList cols={4} gap={8} sx={{ mb: 2 }}>
                                {r.attachments.map((a) => (
                                  <ImageListItem key={a._id}>
                                    {a.type === 'video' ? (
                                      <video src={`${ASSET_BASE_URL}${a.url}`} controls style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                                    ) : (
                                      <img src={`${ASSET_BASE_URL}${a.url}`} alt={a.name} style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                                    )}
                                  </ImageListItem>
                                ))}
                              </ImageList>
                            </>
                          )}

                          {editable && (
                            <Box display="flex" gap={1} alignItems="center">
                              <TextField
                                size="small" type="number" label="Customization Price (USD)"
                                value={priceDraft[r._id] ?? (r.customizationPrice ?? '')}
                                onChange={(e) => setPriceDraft((prev) => ({ ...prev, [r._id]: e.target.value }))}
                              />
                              <Button size="small" variant="contained" startIcon={<Save />} disabled={saving === r._id}
                                onClick={() => savePrice(r._id)}>
                                Save Price
                              </Button>
                              <Button size="small" variant="outlined" color="error" startIcon={<Block />} disabled={saving === r._id}
                                onClick={() => decline(r._id)}>
                                Decline
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {requests.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">No customization requests yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminCustomizationRequests;
